type ErrorSource = 'ErrorBoundary' | 'window.onerror' | 'unhandledrejection' | 'catch-handler'

interface ClientError {
  source: ErrorSource
  message: string
  stack?: string
  url: string
  userAgent: string
  timestamp: string
  count: number
}

const FLUSH_INTERVAL_MS = 10_000
const MAX_BATCH_SIZE = 50

const buffer: ClientError[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

function dedupeKey(err: ClientError): string {
  return `${err.source}|${err.message}|${err.stack ?? ''}`
}

function scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush()
  }, FLUSH_INTERVAL_MS)
}

function flush(): void {
  if (buffer.length === 0) return
  const batch = buffer.splice(0, MAX_BATCH_SIZE)
  const endpoint = `${import.meta.env.VITE_BACKEND_URL}/client-error`

  fetch(endpoint, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({errors: batch}),
    keepalive: true,
  }).catch(() => {
    /* best-effort — don't recurse on reporter failure */
  })
}

export function reportError(source: ErrorSource, message: string, stack?: string): void {
  if (!import.meta.env.PROD) return

  const key = dedupeKey({source, message, stack} as ClientError)
  const existing = buffer.find((e) => dedupeKey(e) === key)

  if (existing) {
    existing.count += 1
    return
  }

  buffer.push({
    source,
    message,
    stack,
    url: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    count: 1,
  })

  if (buffer.length >= MAX_BATCH_SIZE) {
    flush()
  } else {
    scheduleFlush()
  }
}

/**
 * Axios errors carry the outgoing request body on `error.config.data`, and that
 * body can hold PII — the applicant lookup posts a full SSN. Handing the raw
 * error to a logger prints the body to the console. This returns a log-safe
 * stand-in that keeps the useful request context (method, url, status) and drops
 * the body, headers and response payload. Non-request errors pass through
 * untouched so ordinary debugging is unaffected.
 */
function toLogSafe(error: unknown): unknown {
  if (!error || typeof error !== 'object') return error

  const candidate = error as {
    isAxiosError?: boolean
    message?: string
    stack?: string
    config?: {method?: string; url?: string}
    response?: {status?: number}
  }
  if (!candidate.isAxiosError && !candidate.config) return error

  const method = candidate.config?.method?.toUpperCase() ?? 'REQUEST'
  const url = candidate.config?.url ?? 'unknown'
  const status = candidate.response?.status
  const detail = status !== undefined ? `failed with status ${status}` : 'failed'
  const safe = new Error(`${method} ${url} ${detail}: ${candidate.message ?? ''}`.trim())
  safe.stack = candidate.stack
  return safe
}

export function reportCatchError(message: string, error: unknown): void {
  const safe = toLogSafe(error)
  if (!import.meta.env.PROD) {
    console.error(message, safe)
    return
  }
  const errObj = safe instanceof Error ? safe : new Error(String(safe))
  reportError('catch-handler', `${message} ${errObj.message}`, errObj.stack)
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush)
}
